import type {
  GratuityOptionsList,
  Tenant,
  TenantCurrencyCode,
} from "@/services/sharedTypes/tenants"

const { platform } = useDevice()

type AccessControl = {
  transactions: boolean
  reports: boolean
}

export type TenantState = {
  schemaName: string | null
  isDemo: boolean
  name: string | null
  address1: string | null
  address2: string | null
  isProductsEnabled: boolean
  isCashiersEnabled: boolean
  allowLoginWithoutCashier: boolean
  isVatEnabled: boolean
  isServiceChargeEnabled: boolean
  defaultServiceChargePercentage: number | null
  defaultServiceChargeFlat: number | null
  isGratuityEnabled: boolean
  isCashEnabled: boolean
  isTransactionReceiptsEnabled: boolean
  isReceiptQrCodeEnabled: boolean
  isAutomaticMerchantReceiptEnabled: boolean
  isCardReceiptsEnabled: boolean
  isSplitPaymentsEnabled: boolean
  accessControlRestrict: AccessControl
  isUnlinkedRefundsEnabled: boolean
  unlinkedRefundsSingleMaximum: number
  youlendOffer: number | null
  gratuityIsAvailable: boolean
  currency: TenantCurrencyCode
  gratuityOptionsList: GratuityOptionsList
  isUKmerchant: boolean
  hasMultipleDevices: boolean
}

const tenantState = reactive<TenantState>({
  schemaName: null,
  isDemo: false,
  isProductsEnabled: false,
  isCashiersEnabled: false,
  allowLoginWithoutCashier: true,
  isVatEnabled: false,
  name: null,
  address1: null,
  address2: null,
  isServiceChargeEnabled: false,
  defaultServiceChargePercentage: null,
  defaultServiceChargeFlat: null,
  isGratuityEnabled: false,
  isCashEnabled: false,
  isTransactionReceiptsEnabled: false,
  isReceiptQrCodeEnabled: false,
  isAutomaticMerchantReceiptEnabled: false,
  isCardReceiptsEnabled: false,
  isSplitPaymentsEnabled: false,
  accessControlRestrict: {
    transactions: false,
    reports: false,
  },
  isUnlinkedRefundsEnabled: false,
  unlinkedRefundsSingleMaximum: 0,
  youlendOffer: null,
  gratuityIsAvailable: false,
  currency: "GBP",
  gratuityOptionsList: [0, 0, 0, 0],
  isUKmerchant: true,
  hasMultipleDevices: false,
})

const tenantStateRefs = toRefs(tenantState)

const setTenantState = (responseTenantState: Tenant) => {
  tenantState.schemaName = responseTenantState.schemaName
  tenantState.isDemo = responseTenantState.isDemo
  tenantState.isProductsEnabled =
    responseTenantState.isProductsEnabled
  tenantState.isCashiersEnabled =
    responseTenantState.isCashiersEnabled
  tenantState.allowLoginWithoutCashier =
    responseTenantState.allowLoginWithoutCashier
  tenantState.isVatEnabled =
    responseTenantState.isVatEnabled
  tenantState.name = responseTenantState.name
  tenantState.address1 = responseTenantState.address1
  tenantState.address2 = responseTenantState.address2
  tenantState.isServiceChargeEnabled =
    responseTenantState.isServiceChargeEnabled
  tenantState.defaultServiceChargePercentage =
    responseTenantState.defaultServiceChargePercentage
  tenantState.defaultServiceChargeFlat =
    responseTenantState.defaultServiceChargeFlat
  tenantState.isCashEnabled =
    responseTenantState.isCashEnabled
  tenantState.accessControlRestrict = {
    transactions:
      responseTenantState.accessControlRestrictTransactions,
    reports:
      responseTenantState.accessControlRestrictReports,
  }
  tenantState.isTransactionReceiptsEnabled =
    responseTenantState.isTransactionReceiptsEnabled
  tenantState.isReceiptQrCodeEnabled =
    responseTenantState.isReceiptQrCodeEnabled
  tenantState.isCardReceiptsEnabled =
    responseTenantState.isCardReceiptsEnabled
  tenantState.isAutomaticMerchantReceiptEnabled =
    responseTenantState.isAutomaticMerchantReceiptEnabled
  tenantState.isSplitPaymentsEnabled =
    responseTenantState.isSplitPaymentsEnabled

  tenantState.youlendOffer =
    responseTenantState.youlendOffer
  tenantState.isUnlinkedRefundsEnabled =
    responseTenantState.isUnlinkedRefundsEnabled
  tenantState.unlinkedRefundsSingleMaximum =
    responseTenantState.unlinkedRefundsSingleMaximum
  tenantState.gratuityIsAvailable =
    platform !== "paxEvo" ||
    (responseTenantState.evoConfig?.gratuityIsAvailable ??
      false)
  tenantState.isGratuityEnabled =
    tenantState.gratuityIsAvailable &&
    responseTenantState.isGratuityEnabled
  tenantState.currency = responseTenantState.currency
  tenantState.gratuityOptionsList =
    responseTenantState.gratuityOptionsList
  tenantState.hasMultipleDevices =
    responseTenantState.hasMultipleDevices
}

const setAccessControlRestrict = (
  newState: AccessControl
) => {
  tenantState.accessControlRestrict = newState
}

const setIsUKMerchant = (value: boolean) => {
  tenantState.isUKmerchant = value
}

// Want to be able to have { ...tenantRefs }, but don't now consise way to make readonly
// since readonly() unwraps refs?
export const useTenant = () => ({
  tenant: readonly(tenantState),
  isDemo: readonly(tenantStateRefs.isDemo),
  isProductsEnabled: readonly(
    tenantStateRefs.isProductsEnabled
  ),
  isCashiersEnabled: readonly(
    tenantStateRefs.isCashiersEnabled
  ),
  allowLoginWithoutCashier: readonly(
    tenantStateRefs.allowLoginWithoutCashier
  ),
  schemaName: readonly(tenantStateRefs.schemaName),
  name: readonly(tenantStateRefs.name),
  currency: readonly(tenantStateRefs.currency),
  address1: readonly(tenantStateRefs.address1),
  address2: readonly(tenantStateRefs.address2),
  isVatEnabled: readonly(tenantStateRefs.isVatEnabled),
  accessControlRestrict: readonly(
    tenantStateRefs.accessControlRestrict
  ),
  isGratuityEnabled: readonly(
    tenantStateRefs.isGratuityEnabled
  ),
  isServiceChargeEnabled: readonly(
    tenantStateRefs.isServiceChargeEnabled
  ),
  defaultServiceChargePercentage: readonly(
    tenantStateRefs.defaultServiceChargePercentage
  ),
  defaultServiceChargeFlat: readonly(
    tenantStateRefs.defaultServiceChargeFlat
  ),
  isCashEnabled: readonly(tenantStateRefs.isCashEnabled),
  isTransactionReceiptsEnabled: readonly(
    tenantStateRefs.isTransactionReceiptsEnabled
  ),
  isReceiptQrCodeEnabled: readonly(
    tenantStateRefs.isReceiptQrCodeEnabled
  ),
  isCardReceiptsEnabled: readonly(
    tenantStateRefs.isCardReceiptsEnabled
  ),
  youlendOffer: readonly(tenantStateRefs.youlendOffer),
  isUnlinkedRefundsEnabled: readonly(
    tenantStateRefs.isUnlinkedRefundsEnabled
  ),
  unlinkedRefundsSingleMaximum: readonly(
    tenantStateRefs.unlinkedRefundsSingleMaximum
  ),
  gratuityIsAvailable: readonly(
    tenantStateRefs.gratuityIsAvailable
  ),
  isSplitPaymentsEnabled: readonly(
    tenantStateRefs.isSplitPaymentsEnabled
  ),
  gratuityOptionsList: readonly(
    tenantStateRefs.gratuityOptionsList
  ),
  isUKmerchant: readonly(tenantStateRefs.isUKmerchant),
  hasMultipleDevices: readonly(
    tenantStateRefs.hasMultipleDevices
  ),
  setTenantState,
  setAccessControlRestrict,
  setIsUKMerchant,
})
